import React from 'react';
import { FiActivity } from 'react-icons/fi';
import { FaSackDollar } from 'react-icons/fa6';
import { LuScale3D } from 'react-icons/lu';
import { BsGraphUpArrow, BsGraphDownArrow } from 'react-icons/bs';
import { TbZoomMoney } from 'react-icons/tb';
import classNames from 'styles/utils/classNames';
import { roundToDecimals } from 'utils/shared/number/roundToDecimals';

interface Props {
  breakEvenPoints: number[];
  strategyValues: any;
  normalizedProfitFactor: number;
}

export const StrategyMatric: React.FC<Props> = ({
  breakEvenPoints,
  strategyValues,
  normalizedProfitFactor,
}) => {
  const hasErraticBehavior = strategyValues?.hasErraticBehavior;
  const strategyMetrics = strategyValues?.strategyMetrics;
  const maxProfitLoss = strategyValues?.maxProfitLoss;
  const maxProfit = maxProfitLoss?.maxProfit;
  const maxLoss = maxProfitLoss?.maxLoss;

  const probabilityOfProfit = strategyValues?.probabilityOfProfit;

  return (
    <div className="mt-2">
      <div className="mx-auto max-w-7xl rounded-md bg-palette-black-2 py-2.5">
        <div className="grid grid-cols-2 items-center justify-evenly gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          <div className="space-y-1 text-center">
            <div className="text-center text-[10px]">
              {!!strategyMetrics?.netDebit && strategyMetrics.netDebit >= 0
                ? 'Premium Received'
                : 'Premium Paid'}
            </div>
            {!!strategyMetrics?.netDebit && (
              <div className="flex items-center justify-center gap-x-2">
                <FaSackDollar className={classNames('h-3 w-3', 'text-yellow-300')} />
                <span className={classNames('text-[10px] tracking-tight')}>
                  {isFinite(strategyMetrics?.netDebit)
                    ? `$${new Intl.NumberFormat('en-US').format(
                      Math.abs(parseInt(strategyMetrics?.netDebit.toFixed(0), 10)),
                    )}`
                    : strategyMetrics?.netDebit?.toFixed(0)}
                </span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <div className="text-center text-[10px]">Max Profit</div>
            {!!maxProfit && (
              <div className="flex items-center justify-center gap-x-2">
                <span>
                  <BsGraphUpArrow className="h-3 w-3 text-green-500" />
                </span>
                <span className="text-[10px] tracking-tight text-white">
                  {isFinite(maxProfit)
                    ? `$${new Intl.NumberFormat('en-US').format(
                      parseInt(maxProfit.toFixed(0), 10),
                    )}`
                    : maxProfit?.toFixed(0)}
                </span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <div className="text-center text-[10px]">Max Loss</div>
            {!!maxLoss && (
              <div className="flex items-center justify-center gap-x-2">
                <span>
                  <BsGraphDownArrow className="h-3 w-3 text-red-600" />
                </span>
                <span className="text-[10px] tracking-tight text-white">
                  {isFinite(maxLoss)
                    ? `$${new Intl.NumberFormat('en-US').format(
                      Math.abs(parseInt(maxLoss.toFixed(0), 10)),
                    )}`
                    : maxLoss?.toFixed(0)}
                </span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <div className="text-center text-[10px]">Profit Index</div>
            <div className="flex items-center justify-center gap-x-2">
              <span>
                <LuScale3D className="h-4 w-4 text-electric-teal" />
              </span>
              <span className="text-[10px] tracking-tight text-white">
                {normalizedProfitFactor !== undefined && !isNaN(normalizedProfitFactor)
                  ? roundToDecimals(Math.abs(normalizedProfitFactor), 2)
                  : '--'}
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-center text-[10px]">Probability of Profit</div>
            {probabilityOfProfit !== undefined && (
              <div className="flex items-center justify-center gap-x-2">
                <span>
                  <TbZoomMoney className="h-4 w-4 text-top-position" />
                </span>
                <span className="text-[10px] font-semibold tracking-tight text-white">
                  {`${roundToDecimals(probabilityOfProfit * 100, 2)}%`}
                </span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <div className="text-center text-[10px]">Break Even</div>
            <div className="flex items-center justify-center gap-x-2">
              {!!breakEvenPoints && (
                <span>
                  <FiActivity className="h-4 w-4 text-graph-purple" />
                </span>
              )}
              <span className="text-[10px] tracking-tight text-white">
                {hasErraticBehavior
                  ? '-'
                  : breakEvenPoints?.map((bep: number) => roundToDecimals(bep, 2))?.join(', ')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
